import React from "react";
import { CheckCircle, Circle } from "lucide-react";

import { OrderHistoryEntry } from "../../types/orders";

interface OrderHistoryProps {
  history: OrderHistoryEntry[];
}

const OrderHistory: React.FC<OrderHistoryProps> = ({ history }) => {
  // Format date/time
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  // Get status color
  const getStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      new: "text-blue-600 dark:text-blue-400",
      processing: "text-yellow-600 dark:text-yellow-400",
      ready: "text-green-600 dark:text-green-400",
      out_for_delivery: "text-purple-600 dark:text-purple-400",
      delivered: "text-green-600 dark:text-green-400",
      picked_up: "text-green-600 dark:text-green-400",
      cancelled: "text-red-600 dark:text-red-400",
    };

    const lowerCaseStatus = status?.toLowerCase().replace(/ /g, "_");

    return statusMap[lowerCaseStatus] || "text-gray-600 dark:text-gray-400";
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
          Order History
        </h3>
      </div>

      <div className="p-4">
        {history.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400 py-4">
            No history available
          </p>
        ) : (
          <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-3">
            {history.map((entry, index) => (
              <li key={entry.id} className="mb-6 ml-6 last:mb-0">
                <span className="absolute flex items-center justify-center w-6 h-6 bg-gray-100 dark:bg-gray-700 rounded-full -left-3 ring-8 ring-white dark:ring-gray-800">
                  {index === 0 ? (
                    // Latest status icon
                    <CheckCircle className="h-3 w-3 text-primary-600 dark:text-primary-400" />
                  ) : (
                    // Older status icon
                    <Circle className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                  )}
                </span>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="flex justify-between items-start mb-1">
                    <h4
                      className={`text-sm font-semibold ${getStatusColor(entry.new_status)}`}
                    >
                      {entry.new_status}
                    </h4>
                    <time className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDateTime(entry.changed_at)}
                    </time>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Changed from{" "}
                    <span className={getStatusColor(entry.previous_status)}>
                      {entry.previous_status}
                    </span>{" "}
                    by {entry.changed_by}
                  </p>
                  {entry.notes && (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 italic">
                      "{entry.notes}"
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};

export default OrderHistory;
